import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { resetPassword } from '@/controller/auth';
import { toast } from '@/shared/components/UI';
import { useTranslations } from '@/translations/useTranslations.js';

const ResetPassword = () => {
    const { t } = useTranslations();

    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({
        password: '',
        confirm_password: '',
    });
    const [errors, setErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const nav = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [token, setToken] = useState(false);
    const [email, setEmail] = useState(false);

    // Handle form field changes
    const handleFieldChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));

        if (errors[field]) {
            setErrors((prev) => ({
                ...prev,
                [field]: null,
            }));
        }
    };

    // Form validation
    const validateForm = () => {
        const newErrors = {};

        if (!formData.password.trim()) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters';
        }

        if (!formData.confirm_password.trim()) {
            newErrors.confirm_password = 'Confirm password is required';
        } else if (formData.confirm_password.length < 8) {
            newErrors.confirm_password = 'Confirm password must be at least 8 characters';
        } else if (formData.password !== formData.confirm_password) {
            newErrors.confirm_password = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        await onFinish(formData);
    };

    const onFinish = async (v) => {
        setLoading(true);
        try {
            const ret = await resetPassword(token, email, v.password, v.confirm_password);
            if (ret.status === 200) {
                toast.success('Password reset successfully. Please proceed to login.');
                nav('/');
            } else {
                setLoading(false);
                toast.error(ret.data.message);
            }
        } catch (e) {
            //
        }
    };

    useEffect(() => {
        const tk = searchParams.get('token');
        const email = searchParams.get('email');

        setLoading(false);

        if (tk === null || tk === '') {
            nav('/');
        } else {
            //proceed
            setToken(tk);
            setEmail(email);
            searchParams.delete('token');
            searchParams.delete('email');
            setSearchParams(searchParams);
            // check(tk);
        }
    }, []);

    return (
        <div className="px-4 sm:px-8 md:px-16 lg:px-32 xl:px-40 flex flex-1 justify-center py-5 font-sans bg-white">
            <div className="flex flex-col w-full max-w-2xl py-5 flex-1">
                {/* Header Section */}
                <div className="flex flex-wrap justify-between gap-3 p-4">
                    <div>
                        <p className="text-[#111218] tracking-light text-2xl sm:text-3xl lg:text-[32px] font-bold leading-tight m-0">
                            {t('label.reset_password')}
                        </p>
                        <p className="text-[#5f678c] text-sm font-normal leading-normal mt-2">
                            Enter your new password to reset your account.
                        </p>
                    </div>
                </div>

                {/* Loading Overlay */}
                {loading && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 flex items-center space-x-3">
                            <div className="w-6 h-6 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
                            <span className="text-gray-700">Processing...</span>
                        </div>
                    </div>
                )}

                {/* Reset Password Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* New Password Field */}
                    <div className="flex max-w-full flex-wrap items-end gap-4 px-4 py-3">
                        <label className="flex flex-col flex-1">
                            <p className="text-gray-900 text-base font-medium pb-2">
                                {t('form.new_password')}
                                <span className="text-red-500 ml-1">*</span>
                            </p>
                            <div className="relative">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    className={`w-full h-14 rounded-xl border ${errors.password ? 'border-red-500' : 'border-gray-300'} bg-white px-4 pr-12 text-base outline-none focus:border-blue-500 focus-ring transition-colors`}
                                    value={formData.password}
                                    onChange={(e) => handleFieldChange('password', e.target.value)}
                                    placeholder={t('form.password')}
                                    maxLength={50}
                                    disabled={loading}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors duration-200"
                                >
                                    {showPassword ? (
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                                            />
                                        </svg>
                                    ) : (
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                            />
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                            />
                                        </svg>
                                    )}
                                </button>
                            </div>
                            {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
                        </label>
                    </div>

                    {/* Confirm Password Field */}
                    <div className="flex max-w-full flex-wrap items-end gap-4 px-4 py-3">
                        <label className="flex flex-col flex-1">
                            <p className="text-gray-900 text-base font-medium pb-2">
                                {t('form.confirm_password')}
                                <span className="text-red-500 ml-1">*</span>
                            </p>
                            <div className="relative">
                                <input
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    className={`w-full h-14 rounded-xl border ${errors.confirm_password ? 'border-red-500' : 'border-gray-300'} bg-white px-4 pr-12 text-base outline-none focus:border-blue-500 focus-ring transition-colors`}
                                    value={formData.confirm_password}
                                    onChange={(e) => handleFieldChange('confirm_password', e.target.value)}
                                    placeholder={t('form.confirm_password')}
                                    maxLength={50}
                                    disabled={loading}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors duration-200"
                                >
                                    {showConfirmPassword ? (
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                                            />
                                        </svg>
                                    ) : (
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                            />
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                            />
                                        </svg>
                                    )}
                                </button>
                            </div>
                            {errors.confirm_password && (
                                <p className="text-red-500 text-sm mt-1">{errors.confirm_password}</p>
                            )}
                        </label>
                    </div>

                    {/* Submit Button */}
                    <div className="p-4 flex justify-center">
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex min-w-[120px] max-w-[300px] cursor-pointer items-center justify-center overflow-hidden rounded-xl h-12 px-6 bg-gray-50 text-[#121317] border border-gray-300 text-sm font-bold leading-normal transition-colors duration-200 hover:bg-gray-200 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed w-full"
                        >
                            <span className="overflow-hidden">
                                {loading ? 'Processing...' : t('label.reset_password')}
                            </span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ResetPassword;
